import React from 'react';
import { BarChart3, Download, LogOut, RefreshCw, Search, ShieldCheck } from 'lucide-react';
import { useTelegram } from '../hooks/useTelegram';

const ADMIN_KEY_STORAGE = 'jpexgo_admin_key_v1';
const STAFF_SESSION_STORAGE = 'jpexgo_staff_session_v1';
const PAGE_SIZE = 20;

const getApiPath = (path) => {
  if (!path || typeof path !== 'string') return path;
  if (!path.startsWith('/api/')) return path;
  const apiBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
  return apiBase ? `${apiBase}${path}` : path;
};

const apiPost = async (path, body) => {
  const res = await fetch(getApiPath(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Request failed: ${res.status}`);
  return data;
};

const today = () => new Date().toISOString().slice(0, 10);

const formatGroupedNumber = (value, decimals = 2, trimZeros = false) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return '0';
  let fixed = n.toFixed(decimals);
  if (trimZeros) fixed = fixed.replace(/\.00$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
  const [intPart, decPart] = fixed.split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return decPart ? `${grouped}.${decPart}` : grouped;
};

const formatMoney = (value) => `${formatGroupedNumber(value, 2)} сом`;
const formatWeight = (value) => `${Number(value || 0).toFixed(3)} кг`;

const formatDateTime = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const StatCard = ({ label, value, hint, tone = 'default' }) => {
  const tones = {
    default: 'border-stroke bg-white/5',
    yellow: 'border-neon-yellow/25 bg-neon-yellow/10',
    green: 'border-emerald-400/25 bg-emerald-400/10',
    blue: 'border-sky-400/25 bg-sky-400/10',
  };
  return (
    <div className={`rounded-2xl border p-4 ${tones[tone] || tones.default}`}>
      <div className="text-[11px] uppercase tracking-wide text-text-muted">{label}</div>
      <div className="mt-1 text-2xl font-black">{value}</div>
      {hint ? <div className="mt-1 text-xs text-text-secondary">{hint}</div> : null}
    </div>
  );
};

const normalizeSearch = (value) => String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();

const includesQuery = (row, query, keys) => {
  const q = normalizeSearch(query);
  if (!q) return true;
  return keys.some((key) => normalizeSearch(row?.[key]).includes(q));
};

const TabButton = ({ active, onClick, label, count }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex h-10 items-center gap-2 rounded-2xl border px-4 text-sm font-semibold transition ${
      active
        ? 'border-neon-yellow/45 bg-neon-yellow text-black'
        : 'border-stroke bg-white/5 text-text-secondary hover:border-neon-yellow/30 hover:text-text-primary'
    }`}
  >
    {label}
    <span className={`rounded-full px-2 py-0.5 text-[11px] ${active ? 'bg-black/10 text-black' : 'bg-white/10 text-text-secondary'}`}>
      {count}
    </span>
  </button>
);

const Pagination = ({ page, total, pageSize, onPageChange }) => {
  const totalPages = Math.max(1, Math.ceil(Number(total || 0) / pageSize));
  const from = total ? page * pageSize + 1 : 0;
  const to = Math.min(total, (page + 1) * pageSize);

  return (
    <div className="mt-4 flex flex-col gap-2 border-t border-stroke/70 pt-3 text-xs text-text-secondary sm:flex-row sm:items-center sm:justify-between">
      <div>
        Показано {from}-{to} из {total}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(0, page - 1))}
          disabled={page <= 0}
          className="h-9 rounded-xl border border-stroke bg-white/5 px-3 font-semibold text-text-primary disabled:opacity-40"
        >
          Назад
        </button>
        <span>
          {page + 1} / {totalPages}
        </span>
        <button
          type="button"
          onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
          disabled={page >= totalPages - 1}
          className="h-9 rounded-xl border border-stroke bg-white/5 px-3 font-semibold text-text-primary disabled:opacity-40"
        >
          Вперёд
        </button>
      </div>
    </div>
  );
};

const pageItems = (items, page) => items.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

const ManagerLogin = ({ value, password, onChange, onPasswordChange, onSubmit, error, loading }) => (
  <div className="min-h-screen bg-bg px-4 py-8 text-text-primary">
    <div className="mx-auto flex min-h-[70vh] max-w-md items-center">
      <div className="w-full rounded-3xl border border-stroke bg-surface p-5 shadow-[0_18px_80px_rgba(0,0,0,0.35)]">
        <div className="inline-flex items-center gap-2 rounded-full border border-neon-yellow/25 bg-neon-yellow/10 px-3 py-1 text-xs font-semibold text-neon-yellow">
          <ShieldCheck size={15} /> Кабинет менеджера
        </div>
        <h1 className="mt-3 text-2xl font-black">Вход в отчёты</h1>
        <p className="mt-1 text-sm text-text-secondary">
          Введите логин и пароль сотрудника. Старый ADMIN_KEY временно тоже работает.
        </p>
        <input
          value={value}
          onChange={(e) => onChange(e.target.value.trim())}
          placeholder="Логин или ADMIN_KEY"
          className="mt-5 h-12 w-full rounded-2xl border border-stroke bg-[#161616] px-4 text-sm outline-none focus:border-neon-yellow/50"
        />
        <input
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          placeholder="Пароль сотрудника"
          type="password"
          className="mt-3 h-12 w-full rounded-2xl border border-stroke bg-[#161616] px-4 text-sm outline-none focus:border-neon-yellow/50"
        />
        {error ? <div className="mt-3 rounded-2xl border border-red-400/25 bg-red-400/10 p-3 text-sm text-red-100">{error}</div> : null}
        <button
          type="button"
          onClick={onSubmit}
          disabled={!value || loading}
          className="mt-4 h-12 w-full rounded-2xl bg-neon-yellow px-4 text-sm font-bold text-black disabled:opacity-60"
        >
          Войти
        </button>
      </div>
    </div>
  </div>
);

const ManagerReportsPage = () => {
  const { initData } = useTelegram();
  const initDataRaw = initData?.initData || '';
  const [adminKey, setAdminKey] = React.useState(() => {
    try {
      return localStorage.getItem(ADMIN_KEY_STORAGE) || '';
    } catch (_) {
      return '';
    }
  });
  const [staffSessionToken, setStaffSessionToken] = React.useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STAFF_SESSION_STORAGE) || '{}')?.staffSessionToken || '';
    } catch (_) {
      return '';
    }
  });
  const [loginKey, setLoginKey] = React.useState(adminKey);
  const [loginPassword, setLoginPassword] = React.useState('');
  const [country, setCountry] = React.useState('');
  const [dateFrom, setDateFrom] = React.useState(today());
  const [dateTo, setDateTo] = React.useState(today());
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [loginLoading, setLoginLoading] = React.useState(false);
  const [exportLoading, setExportLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [activeTab, setActiveTab] = React.useState('payments');
  const [tableQuery, setTableQuery] = React.useState('');
  const [paymentMethod, setPaymentMethod] = React.useState('');
  const [cashierStatus, setCashierStatus] = React.useState('');
  const [tablePage, setTablePage] = React.useState(0);

  const hasTelegramAuth = typeof initDataRaw === 'string' && initDataRaw.includes('hash=');
  const hasAuth = Boolean(hasTelegramAuth || adminKey || staffSessionToken);
  const authBody = React.useCallback((extra = {}) => {
    if (staffSessionToken) return { staffSessionToken, ...extra };
    return hasTelegramAuth ? { initData: initDataRaw, ...extra } : { adminKey, ...extra };
  }, [adminKey, hasTelegramAuth, initDataRaw, staffSessionToken]);

  const loadReports = React.useCallback(async () => {
    if (!hasAuth) return;
    setLoading(true);
    setError('');
    try {
      const r = await apiPost('/api/manager/reports/summary', authBody({ country: country || null, dateFrom, dateTo }));
      setData(r);
    } catch (e) {
      setError(e?.message || 'Не удалось загрузить отчёты');
      if (!hasTelegramAuth && !staffSessionToken) {
        try {
          localStorage.removeItem(ADMIN_KEY_STORAGE);
        } catch (_) {
          // ignore
        }
        setAdminKey('');
      }
      if (staffSessionToken) {
        try {
          localStorage.removeItem(STAFF_SESSION_STORAGE);
        } catch (_) {
          // ignore
        }
        setStaffSessionToken('');
      }
    } finally {
      setLoading(false);
    }
  }, [authBody, country, dateFrom, dateTo, hasAuth, hasTelegramAuth, staffSessionToken]);

  React.useEffect(() => {
    loadReports();
  }, [loadReports]);

  const login = async () => {
    const nextKey = String(loginKey || '').trim();
    if (!nextKey) return;
    setLoginLoading(true);
    setError('');
    try {
      let auth = { adminKey: nextKey };
      if (loginPassword) {
        const session = await apiPost('/api/staff/login', { login: nextKey, password: loginPassword });
        auth = { staffSessionToken: session.staffSessionToken };
        localStorage.setItem(STAFF_SESSION_STORAGE, JSON.stringify(session));
        localStorage.removeItem(ADMIN_KEY_STORAGE);
        setStaffSessionToken(session.staffSessionToken || '');
      } else {
        localStorage.setItem(ADMIN_KEY_STORAGE, nextKey);
      }
      const r = await apiPost('/api/manager/reports/summary', { ...auth, country: country || null, dateFrom, dateTo });
      setData(r);
      setAdminKey(loginPassword ? '' : nextKey);
      setLoginPassword('');
    } catch (_) {
      try {
        localStorage.removeItem(ADMIN_KEY_STORAGE);
        localStorage.removeItem(STAFF_SESSION_STORAGE);
      } catch (__) {
        // ignore
      }
      setAdminKey('');
      setStaffSessionToken('');
      setError('Неверный логин, пароль или ключ администратора');
    } finally {
      setLoginLoading(false);
    }
  };

  const logout = () => {
    try {
      localStorage.removeItem(ADMIN_KEY_STORAGE);
      localStorage.removeItem(STAFF_SESSION_STORAGE);
    } catch (_) {
      // ignore
    }
    setAdminKey('');
    setStaffSessionToken('');
    setLoginKey('');
    setLoginPassword('');
    setData(null);
  };

  React.useEffect(() => {
    setTablePage(0);
  }, [activeTab, tableQuery, paymentMethod, cashierStatus, country, dateFrom, dateTo]);

  const exportReport = async () => {
    setExportLoading(true);
    setError('');
    try {
      const res = await fetch(getApiPath('/api/manager/reports/export/excel'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(authBody({ country: country || null, dateFrom, dateTo })),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e?.error || `Request failed: ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `manager_report_${country || 'all'}_${dateFrom}_${dateTo}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.message || 'Не удалось скачать отчёт');
    } finally {
      setExportLoading(false);
    }
  };

  if (!hasAuth) {
    return <ManagerLogin value={loginKey} password={loginPassword} onChange={setLoginKey} onPasswordChange={setLoginPassword} onSubmit={login} error={error} loading={loginLoading} />;
  }

  const summary = data?.summary || {};
  const payments = Array.isArray(data?.payments) ? data.payments : [];
  const pickups = Array.isArray(data?.pickups) ? data.pickups : [];
  const pendingOrders = Array.isArray(data?.pendingOrders) ? data.pendingOrders : [];
  const cashierStats = Array.isArray(data?.cashierStats) ? data.cashierStats : [];
  const filteredPayments = payments
    .filter((p) => !paymentMethod || p.method === paymentMethod)
    .filter((p) => includesQuery(p, tableQuery, ['bank_transaction_id', 'track_numbers', 'cashier_name', 'method', 'countries']));
  const filteredPickups = pickups
    .filter((p) => includesQuery(p, tableQuery, ['pickup_code', 'track_number', 'recipient_name', 'phone', 'country', 'cashier_name']));
  const filteredPendingOrders = pendingOrders
    .filter((o) => includesQuery(o, tableQuery, ['track_number', 'recipient_name', 'phone', 'country', 'pickup_code']));
  const filteredCashierStats = cashierStats
    .filter((c) => !cashierStatus || (cashierStatus === 'active' ? c.is_active : !c.is_active))
    .filter((c) => includesQuery(c, tableQuery, ['full_name']));

  const tabCounts = {
    payments: filteredPayments.length,
    pickups: filteredPickups.length,
    pending: filteredPendingOrders.length,
    cashiers: filteredCashierStats.length,
  };
  const pagedPayments = pageItems(filteredPayments, tablePage);
  const pagedPickups = pageItems(filteredPickups, tablePage);
  const pagedPendingOrders = pageItems(filteredPendingOrders, tablePage);
  const pagedCashierStats = pageItems(filteredCashierStats, tablePage);

  return (
    <div className="min-h-screen bg-bg px-4 py-5 text-text-primary">
      <div className="mx-auto max-w-7xl">
        <div className="rounded-3xl border border-stroke bg-surface p-5 shadow-[0_18px_80px_rgba(0,0,0,0.35)]">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-neon-yellow/25 bg-neon-yellow/10 px-3 py-1 text-xs font-semibold text-neon-yellow">
                <BarChart3 size={15} /> Отчёты
              </div>
              <h1 className="mt-3 text-2xl font-black tracking-tight">Оплаты и выдачи</h1>
              <p className="mt-1 max-w-2xl text-sm text-text-secondary">
                Сводка по QR-оплатам, выдачам, готовым заказам и кассирам за выбранный период.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={loadReports} disabled={loading} className="inline-flex h-10 items-center gap-2 rounded-xl border border-neon-yellow/30 bg-neon-yellow/10 px-3 text-sm font-semibold text-neon-yellow disabled:opacity-60">
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Обновить
              </button>
              <button type="button" onClick={exportReport} disabled={exportLoading} className="inline-flex h-10 items-center gap-2 rounded-xl border border-stroke bg-white/5 px-3 text-sm font-semibold disabled:opacity-60">
                <Download size={16} /> Скачать Excel
              </button>
              {!hasTelegramAuth ? (
                <button type="button" onClick={logout} className="inline-flex h-10 items-center gap-2 rounded-xl border border-stroke bg-white/5 px-3 text-sm font-semibold">
                  <LogOut size={16} /> Выйти
                </button>
              ) : null}
            </div>
          </div>

          <div className="mt-5 grid gap-3 md:grid-cols-[170px_1fr_1fr_auto]">
            <label className="block">
              <span className="text-xs text-text-secondary">Страна</span>
              <select value={country} onChange={(e) => setCountry(e.target.value)} className="mt-1 h-11 w-full rounded-2xl border border-stroke bg-[#161616] px-3 text-sm outline-none focus:border-neon-yellow/50">
                <option value="">Все</option>
                <option value="KG">Кыргызстан</option>
                <option value="KZ">Казахстан</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-text-secondary">С даты</span>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1 h-11 w-full rounded-2xl border border-stroke bg-[#161616] px-3 text-sm outline-none focus:border-neon-yellow/50" />
            </label>
            <label className="block">
              <span className="text-xs text-text-secondary">По дату</span>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1 h-11 w-full rounded-2xl border border-stroke bg-[#161616] px-3 text-sm outline-none focus:border-neon-yellow/50" />
            </label>
            <div className="flex items-end">
              <button type="button" onClick={loadReports} disabled={loading} className="h-11 rounded-2xl bg-neon-yellow px-4 text-sm font-bold text-black disabled:opacity-60">Показать</button>
            </div>
          </div>

          {error ? <div className="mt-4 rounded-2xl border border-red-400/25 bg-red-400/10 p-3 text-sm text-red-100">{error}</div> : null}
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          <StatCard label="Оплаты" value={summary.payments_count || 0} hint={formatMoney(summary.total_som)} tone="green" />
          <StatCard label="QR-оплаты" value={summary.qr_count || 0} hint={formatMoney(summary.qr_som)} tone="blue" />
          <StatCard label="Выдачи" value={summary.pickups_count || 0} hint={`${formatMoney(summary.pickups_som)} / ${formatWeight(summary.pickups_weight_kg)}`} tone="yellow" />
          <StatCard label="Готово к выдаче" value={summary.ready_count || 0} hint={`${formatMoney(summary.ready_som)} / ${formatWeight(summary.ready_weight_kg)}`} />
          <StatCard label="Ожидают оплаты" value={pendingOrders.length} hint={formatMoney(pendingOrders.reduce((sum, item) => sum + Number(item.amount_som || 0), 0))} />
        </div>

        <div className="mt-4 rounded-3xl border border-stroke bg-surface p-4">
          <div className="flex flex-wrap gap-2">
            <TabButton active={activeTab === 'payments'} onClick={() => setActiveTab('payments')} label="Оплаты" count={tabCounts.payments} />
            <TabButton active={activeTab === 'pickups'} onClick={() => setActiveTab('pickups')} label="Выдачи" count={tabCounts.pickups} />
            <TabButton active={activeTab === 'pending'} onClick={() => setActiveTab('pending')} label="Ожидают оплаты" count={tabCounts.pending} />
            <TabButton active={activeTab === 'cashiers'} onClick={() => setActiveTab('cashiers')} label="Кассиры" count={tabCounts.cashiers} />
          </div>

          <div className="mt-4 grid gap-3 lg:grid-cols-[1fr_220px]">
            <label className="relative block">
              <Search size={17} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
              <input
                value={tableQuery}
                onChange={(e) => setTableQuery(e.target.value)}
                placeholder="Поиск по треку, ФИО, телефону, коду, кассиру или transaction ID"
                className="h-11 w-full rounded-2xl border border-stroke bg-[#161616] pl-11 pr-4 text-sm outline-none focus:border-neon-yellow/50"
              />
            </label>

            {activeTab === 'payments' ? (
              <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="h-11 w-full rounded-2xl border border-stroke bg-[#161616] px-3 text-sm outline-none focus:border-neon-yellow/50">
                <option value="">Все методы оплаты</option>
                <option value="QR">QR</option>
              </select>
            ) : activeTab === 'cashiers' ? (
              <select value={cashierStatus} onChange={(e) => setCashierStatus(e.target.value)} className="h-11 w-full rounded-2xl border border-stroke bg-[#161616] px-3 text-sm outline-none focus:border-neon-yellow/50">
                <option value="">Все кассиры</option>
                <option value="active">Активные</option>
                <option value="inactive">Отключённые</option>
              </select>
            ) : (
              <div className="hidden lg:block" />
            )}
          </div>
        </div>

        <div className="mt-4 grid gap-4 xl:grid-cols-2">
          <section className={`${activeTab === 'payments' ? '' : 'hidden'} rounded-3xl border border-stroke bg-surface p-4 xl:col-span-2`}>
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2 className="text-lg font-black">Оплаты</h2>
              <span className="text-xs text-text-secondary">{filteredPayments.length} записей</span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="text-xs uppercase text-text-muted">
                  <tr>
                    <th className="whitespace-nowrap px-3 py-2">Дата</th>
                    <th className="whitespace-nowrap px-3 py-2">Метод</th>
                    <th className="whitespace-nowrap px-3 py-2">Сумма</th>
                    <th className="whitespace-nowrap px-3 py-2">Треки</th>
                    <th className="whitespace-nowrap px-3 py-2">Кассир</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedPayments.map((p) => (
                    <tr key={p.id} className="border-t border-stroke/70">
                      <td className="whitespace-nowrap px-3 py-2 text-text-secondary">{formatDateTime(p.paid_at)}</td>
                      <td className="whitespace-nowrap px-3 py-2">{p.method || '-'}</td>
                      <td className="whitespace-nowrap px-3 py-2 font-semibold">{formatMoney(p.amount_som)}</td>
                      <td className="max-w-[240px] truncate px-3 py-2 text-text-secondary">{p.track_numbers || '-'}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-text-secondary">{p.cashier_name || '-'}</td>
                    </tr>
                  ))}
                  {!filteredPayments.length ? <tr><td colSpan="5" className="px-3 py-8 text-center text-text-secondary">Оплат за период нет.</td></tr> : null}
                </tbody>
              </table>
            </div>
            <Pagination page={tablePage} total={filteredPayments.length} pageSize={PAGE_SIZE} onPageChange={setTablePage} />
          </section>

          <section className={`${activeTab === 'pickups' ? '' : 'hidden'} rounded-3xl border border-stroke bg-surface p-4 xl:col-span-2`}>
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2 className="text-lg font-black">Выдачи</h2>
              <span className="text-xs text-text-secondary">{filteredPickups.length} записей</span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="text-xs uppercase text-text-muted">
                  <tr>
                    <th className="whitespace-nowrap px-3 py-2">Дата</th>
                    <th className="whitespace-nowrap px-3 py-2">Код</th>
                    <th className="whitespace-nowrap px-3 py-2">Трек</th>
                    <th className="whitespace-nowrap px-3 py-2">Сумма</th>
                    <th className="whitespace-nowrap px-3 py-2">Кассир</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedPickups.map((p) => (
                    <tr key={p.id} className="border-t border-stroke/70">
                      <td className="whitespace-nowrap px-3 py-2 text-text-secondary">{formatDateTime(p.picked_up_at)}</td>
                      <td className="whitespace-nowrap px-3 py-2 font-mono font-black">{p.pickup_code || '-'}</td>
                      <td className="whitespace-nowrap px-3 py-2">{p.track_number || '-'}</td>
                      <td className="whitespace-nowrap px-3 py-2 font-semibold">{formatMoney(p.amount_som)}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-text-secondary">{p.cashier_name || '-'}</td>
                    </tr>
                  ))}
                  {!filteredPickups.length ? <tr><td colSpan="5" className="px-3 py-8 text-center text-text-secondary">Выдач за период нет.</td></tr> : null}
                </tbody>
              </table>
            </div>
            <Pagination page={tablePage} total={filteredPickups.length} pageSize={PAGE_SIZE} onPageChange={setTablePage} />
          </section>
        </div>

        <div className="mt-4 grid gap-4 xl:grid-cols-2">
          <section className={`${activeTab === 'pending' ? '' : 'hidden'} rounded-3xl border border-stroke bg-surface p-4 xl:col-span-2`}>
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2 className="text-lg font-black">Ожидают оплаты</h2>
              <span className="text-xs text-text-secondary">{filteredPendingOrders.length} записей</span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="text-xs uppercase text-text-muted">
                  <tr>
                    <th className="whitespace-nowrap px-3 py-2">Трек</th>
                    <th className="whitespace-nowrap px-3 py-2">ФИО</th>
                    <th className="whitespace-nowrap px-3 py-2">Страна</th>
                    <th className="whitespace-nowrap px-3 py-2">Сумма</th>
                    <th className="whitespace-nowrap px-3 py-2">Код</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedPendingOrders.map((o) => (
                    <tr key={o.id} className="border-t border-stroke/70">
                      <td className="whitespace-nowrap px-3 py-2">{o.track_number || '-'}</td>
                      <td className="max-w-[220px] truncate px-3 py-2 text-text-secondary">{o.recipient_name || '-'}</td>
                      <td className="whitespace-nowrap px-3 py-2">{o.country || '-'}</td>
                      <td className="whitespace-nowrap px-3 py-2 font-semibold">{formatMoney(o.amount_som)}</td>
                      <td className="whitespace-nowrap px-3 py-2 font-mono font-black">{o.pickup_code || '-'}</td>
                    </tr>
                  ))}
                  {!filteredPendingOrders.length ? <tr><td colSpan="5" className="px-3 py-8 text-center text-text-secondary">Заказов, ожидающих оплату, нет.</td></tr> : null}
                </tbody>
              </table>
            </div>
            <Pagination page={tablePage} total={filteredPendingOrders.length} pageSize={PAGE_SIZE} onPageChange={setTablePage} />
          </section>

          <section className={`${activeTab === 'cashiers' ? '' : 'hidden'} rounded-3xl border border-stroke bg-surface p-4 xl:col-span-2`}>
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2 className="text-lg font-black">Кассиры за период</h2>
              <span className="text-xs text-text-secondary">{filteredCashierStats.length} записей</span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="text-xs uppercase text-text-muted">
                  <tr>
                    <th className="whitespace-nowrap px-3 py-2">Кассир</th>
                    <th className="whitespace-nowrap px-3 py-2">Статус</th>
                    <th className="whitespace-nowrap px-3 py-2">Выдач</th>
                    <th className="whitespace-nowrap px-3 py-2">Сумма</th>
                    <th className="whitespace-nowrap px-3 py-2">Вес</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedCashierStats.map((c) => (
                    <tr key={c.id} className="border-t border-stroke/70">
                      <td className="whitespace-nowrap px-3 py-2">{c.full_name || '-'}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-text-secondary">{c.is_active ? 'Активен' : 'Отключён'}</td>
                      <td className="whitespace-nowrap px-3 py-2 font-semibold">{c.pickups_count || 0}</td>
                      <td className="whitespace-nowrap px-3 py-2">{formatMoney(c.pickups_som)}</td>
                      <td className="whitespace-nowrap px-3 py-2">{formatWeight(c.pickups_weight_kg)}</td>
                    </tr>
                  ))}
                  {!filteredCashierStats.length ? <tr><td colSpan="5" className="px-3 py-8 text-center text-text-secondary">Кассиров пока нет.</td></tr> : null}
                </tbody>
              </table>
            </div>
            <Pagination page={tablePage} total={filteredCashierStats.length} pageSize={PAGE_SIZE} onPageChange={setTablePage} />
          </section>
        </div>
      </div>
    </div>
  );
};

export default ManagerReportsPage;
